"use client";

import Link from "next/link";
import {
  ArrowRight,
  CalendarDays,
  HardHat,
  Home,
  Phone,
  Stethoscope,
} from "lucide-react";

import Footer from "./Footer";
import { useLanguage } from "../i18n/LanguageProvider";
import { CONTACT } from "../data/contact";

/* ============================================================
   TAMPILAN HALAMAN 404

   ============================================================
   TEKSNYA TIDAK MEMAKAI dictionary.js

   Berkas ini memakai objek TEKS berisi { id, en } di bawah, bukan
   t("...") - pola yang sudah dipakai IgdContent, MCUContent,
   LaboratoriumContent, dan SwabPcrContent di proyek ini.

   Alasannya: teks di sini hanya dipakai satu berkas dan tidak akan
   pernah dipakai berkas lain. Menaruhnya di dictionary.js berarti
   menambah delapan belas kunci ke berkas 1100 baris yang sudah padat,
   demi teks yang tidak dibagi ke siapa pun.

   Kalau nanti kamu ingin memindahkannya ke dictionary.js, silakan -
   yang penting jangan setengah-setengah, karena teks yang tersebar di
   dua sistem lebih sulit dirawat daripada di salah satu saja.

   ============================================================
   >>> JANGAN TULIS className SEBAGAI STRING MULTI-BARIS. <<<
   Proyek ini berakhiran baris CRLF. String class yang dipecah beberapa
   baris ikut membawa \r\n ke dalam nilai atribut, dan itu memicu
   hydration error. Kalau perlu dipecah, pakai ["...", "..."].join(" ").
   ============================================================ */

const TEKS = {
  kode: { id: "Kesalahan 404", en: "Error 404" },

  judul: {
    id: "Sedang Dalam Proses Pengembangan",
    en: "This Page Is Still Being Built",
  },

  olehTim: {
    id: "Oleh Tim IT RS Jakarta Medical Center",
    en: "By the IT Team of Jakarta Medical Center Hospital",
  },

  /* Menyebut DUA kemungkinan, bukan satu. Pengunjung yang salah ketik
     alamat juga mendarat di sini, dan memberitahunya bahwa halaman itu
     "sedang dikembangkan" adalah keterangan yang keliru. */
  maaf: {
    id: "Mohon maaf atas ketidaknyamanannya. Halaman yang Anda tuju belum tersedia.",
    en: "We apologise for the inconvenience. The page you are looking for is not available yet, or its address has changed. Please use one of the links below.",
  },

  beranda: { id: "Kembali ke Beranda", en: "Back to Home" },
  layanan: { id: "Lihat Semua Layanan", en: "View All Services" },
  jadwal: { id: "Jadwal Dokter", en: "Doctor Schedule" },

  bantuanJudul: { id: "Butuh bantuan sekarang?", en: "Need help right now?" },
  bantuanTeks: {
    id: "Hubungi kami untuk informasi layanan, jadwal dokter, atau kondisi gawat darurat.",
    en: "Call us for service information, doctor schedules, or emergencies.",
  },
  teleponAria: { id: "Telepon {nomor}", en: "Call {nomor}" },
  igd24: { id: "IGD buka 24 jam", en: "Emergency department open 24 hours" },
};

function pilih(nilai, lang) {
  return nilai[lang] || nilai.id;
}

export default function NotFoundContent() {
  const { lang } = useLanguage();
  const T = (kunci) => pilih(TEKS[kunci], lang);

  /* Padding atas = tinggi header fixed, angkanya disalin dari
     app/page.jsx. Kalau tinggi di Header.jsx berubah, semua halaman
     yang memakai angka ini harus ikut diubah. */
  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      <main className="flex flex-1 items-center px-4 py-14 sm:px-6 md:py-20 lg:px-10">
        <div className="mx-auto w-full max-w-3xl text-center">

          {/* IKON, bukan angka 404 raksasa.

             Angka besar adalah bahasa halaman error - ia memberi tahu
             pengunjung bahwa ada yang rusak. Ikon pekerja dengan helm
             memberi tahu bahwa ada yang sedang dikerjakan. Keduanya
             menyampaikan hal berbeda, dan yang kedua yang benar di
             sini.

             Bentuk kelopak (satu sudut siku) mengulang bentuk yang
             dipakai kartu Fasilitas dan dudukan ikon Kenapa Kami -
             kosakata visual yang sama, jadi halaman ini tetap terasa
             bagian dari situs yang sama meski isinya berbeda. */}
          <div className="mx-auto mb-7 flex h-20 w-20 items-center justify-center rounded-full rounded-br-none bg-[var(--c-tint)] ring-1 ring-inset ring-[var(--c-brand-line-2)] md:h-24 md:w-24">
            <HardHat className="h-9 w-9 text-[var(--c-brand-mid)] md:h-11 md:w-11" strokeWidth={1.5} aria-hidden="true" />
          </div>

          {/* Kode error tetap ditampilkan, tapi kecil. Ia berguna untuk
             siapa pun yang melaporkan masalah ini ke tim IT, dan tidak
             berguna untuk pengunjung biasa - jadi ukurannya mengikuti
             kegunaannya, bukan mengikuti kebiasaan. */}
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--c-faint)] sm:text-xs">
            {T("kode")}
          </p>

          <h1 className="mb-3 text-balance text-2xl font-bold leading-[1.15] tracking-tight text-[var(--c-ink)] sm:text-3xl md:text-4xl">
            {T("judul")}
          </h1>

          <p className="mb-6 text-sm font-semibold text-[var(--c-brand)] sm:text-base">
            {T("olehTim")}
          </p>

          {/* Emoji 🙏 di dalam span ber-aria-hidden.

             Pembaca layar melafalkan emoji dengan nama teknisnya -
             "folded hands" atau "person with folded hands" - yang
             menyela kalimat permintaan maafnya alih-alih memperkuatnya.
             Kata "Mohon maaf" di awal kalimat sudah menyampaikan hal
             yang sama, jadi emojinya murni untuk mata. */}
          <p className="mx-auto mb-9 max-w-xl text-pretty text-sm leading-relaxed text-[var(--c-body)] sm:text-base md:mb-11">
            <span aria-hidden="true" className="mr-1.5">🙏</span>
            {T("maaf")}
          </p>

          {/* TIGA JALAN KELUAR, bukan satu tombol "kembali".

             Pengunjung yang mendarat di sini datang dari hasil
             pencarian atau dari tautan di menu, bukan dari halaman
             sebelumnya - jadi "kembali" sering tidak berarti apa-apa
             baginya. Ketiga tautan ini adalah halaman yang paling
             sering dicari di situs rumah sakit.

             Ketiganya SUDAH ADA di proyek ini: /, /layanan, dan
             /jadwal-dokter. Jangan menambahkan tautan ke halaman yang
             belum ada - halaman 404 yang menuju 404 lain adalah cara
             tercepat membuat pengunjung menyerah. */}
          <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <Link
              href="/"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--c-brand)] px-6 py-3 text-sm font-bold text-white transition hover:bg-[var(--c-brand-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] motion-reduce:transition-none md:text-base"
            >
              <Home className="h-4 w-4" aria-hidden="true" />
              {T("beranda")}
            </Link>

            <Link
              href="/layanan"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-[var(--c-brand)] ring-1 ring-[var(--c-line)] transition hover:ring-[var(--c-brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] motion-reduce:transition-none md:text-base"
            >
              <Stethoscope className="h-4 w-4" aria-hidden="true" />
              {T("layanan")}
            </Link>

            <Link
              href="/jadwal-dokter"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-[var(--c-brand)] ring-1 ring-[var(--c-line)] transition hover:ring-[var(--c-brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] motion-reduce:transition-none md:text-base"
            >
              <CalendarDays className="h-4 w-4" aria-hidden="true" />
              {T("jadwal")}
            </Link>
          </div>

          {/* NOMOR TELEPON, dan ini bukan hiasan.

             Sebagian pengunjung yang tersesat di situs rumah sakit
             sedang mencari sesuatu yang mendesak. Menyodorkan nomor
             yang bisa langsung ditekan lebih berguna daripada tautan
             navigasi mana pun di atas.

             Nomornya diambil dari app/data/contact.js - sumber tunggal
             yang sama dengan Header dan Footer, jadi ia tidak akan
             menyimpang saat nomornya berubah. */}
          <div className="mx-auto mt-11 max-w-xl rounded-2xl bg-[var(--c-surface-2)] p-5 ring-1 ring-[var(--c-line)] md:mt-14 md:p-6">
            <p className="mb-1.5 text-sm font-bold text-[var(--c-ink)] md:text-base">
              {T("bantuanJudul")}
            </p>
            <p className="mb-4 text-sm leading-relaxed text-[var(--c-body)]">
              {T("bantuanTeks")}
            </p>

            <a
              href={CONTACT.phoneHref}
              aria-label={pilih(TEKS.teleponAria, lang).replace("{nomor}", CONTACT.phoneLabel)}
              className="inline-flex items-center gap-2.5 text-base font-bold text-[var(--c-brand)] underline-offset-4 transition hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] md:text-lg"
            >
              <Phone className="h-5 w-5 shrink-0" aria-hidden="true" />
              {CONTACT.phoneLabel}
              <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
            </a>

            <p className="mt-2 text-xs text-[var(--c-muted)] sm:text-sm">
              {T("igd24")}
            </p>
          </div>

        </div>
      </main>

      <Footer />

    </div>
  );
}
